import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { ARTISTS } from "@/data/artists";
import MusicalCollectionBar from "@/components/MusicalCollectionBar";
import { inferGenres, artistMatchesFilters } from "@/lib/genres";
import { buildTracklist } from "@/lib/tracks";

const AUDIO_BASE = "/samples/musical-collection";

/**
 * Our Artists — The Resilience Studios Musical Collection.
 * Founder plain-ink 2026-07-02+: on-site channel, by-genre + by-band toggles,
 * persistent bottom-bar player.
 */
export default function Artists() {
  return (
    <Layout>
      <MusicalCollectionBar>
        {({
          activeGenres,
          disabledBands,
          genres,
          toggleGenre,
          clearGenres,
          playlist,
          currentBandSlug,
          currentTrackSlug,
          onPlayTrack,
          catalogReady,
          availableSet,
        }) => (
          <>
            {/* HERO */}
            <section
              data-testid="artists-hero"
              className="relative pt-20 pb-12 sm:pt-28 sm:pb-16 border-b border-white/[0.05]"
            >
              <div className="absolute inset-0 pointer-events-none">
                <div
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] opacity-[0.09] blur-3xl"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 0%,#ff5a00,transparent 60%)",
                  }}
                />
              </div>
              <div className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.45em] text-[#ff5a00]">
                  The Resilience Studios Musical Collection
                </p>
                <h1
                  data-testid="artists-headline"
                  className="font-display uppercase leading-[0.92] mt-6 text-[#ededed]"
                  style={{
                    fontSize: "clamp(2.5rem, 8vw, 6rem)",
                    letterSpacing: "-0.005em",
                  }}
                >
                  37 Bands.
                  <br />
                  <span className="italic text-[#ff5a00]">One forge.</span>
                </h1>
                <p className="font-display italic text-lg sm:text-2xl text-[#a1a1aa] mt-8 max-w-3xl leading-relaxed">
                  Every voice here was built in-house. Curate a channel by
                  genre or by band — the bottom bar plays the collection you
                  turn on.
                </p>

                {/* GENRE CHIPS */}
                <div className="mt-8" data-testid="collection-genre-chips">
                  <div className="flex items-center gap-3 mb-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">
                      By Genre
                    </p>
                    {activeGenres.size > 0 && (
                      <button
                        onClick={clearGenres}
                        data-testid="collection-clear-genres"
                        className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00] hover:text-[#ffbb00] transition-colors"
                      >
                        Clear ({activeGenres.size})
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {genres.map((g) => {
                      const on = activeGenres.has(g);
                      return (
                        <button
                          key={g}
                          onClick={() => toggleGenre(g)}
                          data-testid={`collection-genre-${g
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")}`}
                          className={`font-mono text-[10px] uppercase tracking-[0.24em] px-3 py-2 border transition-colors ${
                            on
                              ? "bg-[#ff5a00] text-black border-[#ff5a00]"
                              : "text-[#a1a1aa] hover:text-[#ededed] border-white/10 hover:border-white/30"
                          }`}
                        >
                          {g}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-6 font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa]">
                  <span data-testid="artists-count">
                    {ARTISTS.length} acts
                  </span>
                  <span className="text-[#3f3f46]">·</span>
                  <span data-testid="artists-track-count">
                    {catalogReady === true
                      ? `Collection playlist · ${playlist.length} tracks`
                      : "Collection playlist · rebuilding"}
                  </span>
                  <span className="text-[#3f3f46]">·</span>
                  <span className="italic text-[#c8c8ce]">
                    Quiet and proof at the same time
                  </span>
                </div>
              </div>
            </section>

            {/* GRID · TurnDash pinned last (per Founder 2026-08-13; hidden slot). */}
            <section className="max-w-[1400px] mx-auto px-4 sm:px-8 py-14 sm:py-20 pb-32">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {ARTISTS
                  .filter((a) => artistMatchesFilters(a, activeGenres, disabledBands))
                  .slice()
                  .sort((a, b) => {
                    const at = a.slug === "turndash" ? 1 : 0;
                    const bt = b.slug === "turndash" ? 1 : 0;
                    return at - bt;
                  })
                  .map((a, i) => (
                  <ArtistCard
                    key={a.id}
                    artist={a}
                    index={i}
                    currentTrackSlug={
                      currentBandSlug === a.slug ? currentTrackSlug : null
                    }
                    onPlayTrack={onPlayTrack}
                    catalogReady={catalogReady}
                    availableSet={availableSet}
                  />
                ))}
              </div>
              {ARTISTS.filter((a) =>
                artistMatchesFilters(a, activeGenres, disabledBands)
              ).length === 0 && (
                <div
                  data-testid="collection-empty"
                  className="text-center py-24 font-display italic text-lg text-[#a1a1aa]"
                >
                  No bands match this filter. Loosen a genre chip or enable a
                  band from the drawer at the bottom.
                </div>
              )}

              {/* FOUNDER'S FOOTNOTE — plain-ink 2026-08-13.
                  Secret/trivia block at the very bottom of Our Artists. */}
              <footer
                data-testid="artists-founder-footer"
                className="mt-24 pt-10 border-t border-white/[0.05] max-w-3xl mx-auto"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-4">
                  A note from the Founder
                </p>
                <p className="font-display italic text-base sm:text-lg text-[#a1a1aa] leading-relaxed">
                  Hey there, Searcher, you Vigilant user, you! Here's a
                  secret/trivial from Resilience: these 37 bands are not made
                  to be good, or sell — they're examples of the limitless
                  capability of AI, and why the traditional music industry and
                  studios are obsolete:
                  <span className="text-[#ededed] not-italic font-body">
                    {" "}I spent six hours on the combined
                    catalogs / albums / songs / treatments / videos for all 37
                    bands. One session, six hours, for all of it.{" "}
                  </span>
                  If I actually spent time on it, they'd be the best music you
                  ever heard.{" "}
                  <span className="text-[#ff5a00] not-italic font-body">
                    Which is what YOU should go do.
                  </span>
                </p>
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.42em] text-[#52525b]">
                  — The Founder
                </p>
              </footer>
            </section>
          </>
        )}
      </MusicalCollectionBar>
    </Layout>
  );
}

function ArtistCard({ artist, index, currentTrackSlug, onPlayTrack, catalogReady, availableSet }) {
  const heroPlaying =
    artist.heroTrack && currentTrackSlug === artist.heroTrack.slug;
  const isHidden = artist.slug === "turndash";
  const [videoBroken, setVideoBroken] = useState(false);
  const [imageBroken, setImageBroken] = useState(false);
  const canShowVideo = !!artist.heroVideo && !videoBroken;
  const canShowImage = !!artist.heroImage && !imageBroken;
  // Manifest-filtered playable-track pick. Founder plain-ink 2026-08-29 —
  // ~4 cards had hero slugs whose mp3 wasn't on disk, so clicking Play
  // hard-failed silently. When the manifest is loaded, prefer the hero track
  // when its mp3 exists; else fall back to the first tracks[] entry with an
  // on-disk mp3. When neither exists, the hero button silences itself.
  const isPlayable = (slug) => !availableSet || availableSet.has(slug);
  // Founder plain-ink 2026-02-15: expand from the manifest so full albums
  // render, not just the 4 hand-authored featured slugs.
  const playableTracks = buildTracklist(artist, availableSet);
  const playableHero =
    artist.heroTrack && isPlayable(artist.heroTrack.slug)
      ? artist.heroTrack
      : playableTracks[0] || null;
  // Honesty gate — when the persistent tracks dir is empty (catalogReady=false)
  // every play button is a silent no-op. Disable them visibly so the surface
  // never lies. Founder plain-ink 2026-08-22 honesty-gate carry-through.
  // Null state (first-paint before probe) also disables so we never render a
  // brief window where the buttons look playable.
  const playsDisabled = catalogReady !== true;
  const heroDisabled = playsDisabled || !playableHero;
  return (
    <motion.article
      data-testid={`artist-card-${artist.slug}`}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{
        duration: 0.5,
        delay: (index % 12) * 0.03,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="group relative bg-[#0b0b0d] border border-white/[0.07] hover:border-white/20 transition-colors overflow-hidden"
    >
      <div
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{ background: artist.accent }}
        aria-hidden
      />

      <div
        className="relative aspect-video overflow-hidden bg-black"
        style={{
          background: `radial-gradient(circle at 30% 30%, ${artist.accent}55, ${artist.accent}18 45%, transparent 75%), #050505`,
        }}
      >
        {canShowVideo ? (
          <video
            src={artist.heroVideo}
            data-testid={`artist-hero-video-${artist.slug}`}
            className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            onError={() => setVideoBroken(true)}
          />
        ) : canShowImage ? (
          <img
            src={artist.heroImage}
            alt={artist.heroImageAlt || artist.name}
            data-testid={`artist-hero-image-${artist.slug}`}
            className="absolute inset-0 w-full h-full object-cover object-top opacity-80 group-hover:opacity-100 transition-opacity duration-500"
            loading="lazy"
            onError={() => setImageBroken(true)}
          />
        ) : (
          // Typographic fallback — no hero art on disk yet. Show a bold band-
          // name plate on the accent palette. Founder plain-ink 2026-08-13.
          <div
            data-testid={`artist-hero-fallback-${artist.slug}`}
            className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center"
            style={{
              background: `linear-gradient(135deg, ${artist.accent}30 0%, transparent 55%, ${artist.accent}18 100%), #050505`,
            }}
          >
            <span
              className="font-display uppercase leading-[0.9] drop-shadow-[0_2px_18px_rgba(0,0,0,0.7)]"
              style={{
                color: artist.accent,
                fontSize: "clamp(1.5rem, 3.4vw, 2.4rem)",
                letterSpacing: "0.02em",
              }}
            >
              {artist.name}
            </span>
            <span className="mt-3 font-mono text-[9px] uppercase tracking-[0.32em] text-white/50">
              Hero reel · in the forge
            </span>
          </div>
        )}

        <div
          className="absolute inset-0 opacity-[0.14] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
          }}
        />

        <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.32em] text-white/70 backdrop-blur-sm bg-black/40 px-2 py-1">
          {isHidden
            ? "TD / friends-only"
            : `${String(artist.id).padStart(2, "0")} / 37`}
        </div>

        <div className="absolute top-4 right-4 font-mono text-[9px] tracking-[0.28em] text-white/50 border border-white/10 px-2 py-1 rounded-sm backdrop-blur-sm bg-black/40">
          {(inferGenres(artist)[0] || "SOLO").toUpperCase()}
        </div>

        <button
          type="button"
          onClick={() =>
            !heroDisabled &&
            playableHero &&
            onPlayTrack(artist.slug, playableHero.slug)
          }
          disabled={heroDisabled}
          data-testid={`artist-play-${artist.slug}`}
          aria-label={
            heroDisabled
              ? "Track catalog rebuilding — play unavailable"
              : `Play hero single for ${artist.name}`
          }
          title={
            heroDisabled
              ? "Track catalog rebuilding · refresh once the bake lands"
              : ""
          }
          className={`absolute inset-0 flex items-center justify-center group/play ${
            heroDisabled ? "cursor-not-allowed" : ""
          }`}
        >
          <span
            className={`w-16 h-16 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
              heroDisabled
                ? "border-white/20 opacity-40"
                : heroPlaying
                ? "border-[#ff5a00] scale-110"
                : "border-white/50 group-hover/play:border-white group-hover/play:scale-105"
            }`}
            style={
              heroPlaying && !playsDisabled
                ? { boxShadow: `0 0 40px ${artist.accent}88` }
                : {}
            }
          >
            {heroPlaying ? (
              <span className="flex gap-1.5">
                <span className="w-1.5 h-6 bg-[#ff5a00]" />
                <span className="w-1.5 h-6 bg-[#ff5a00]" />
              </span>
            ) : (
              <span
                className="w-0 h-0 ml-1.5"
                style={{
                  borderLeft: "14px solid #ededed",
                  borderTop: "8px solid transparent",
                  borderBottom: "8px solid transparent",
                }}
              />
            )}
          </span>
        </button>

        <div className="absolute bottom-3 left-4 right-4 font-mono text-[10px] uppercase tracking-[0.28em] text-white/70 truncate">
          {playsDisabled ? (
            <span className="text-white/40">Track catalog rebuilding</span>
          ) : artist.heroTrack ? (
            <>
              Now cued ·{" "}
              <span className="text-white">{artist.heroTrack.title}</span>
            </>
          ) : (
            <span className="text-white/40">Track menu queued for bake</span>
          )}
        </div>
      </div>

      <div className="px-5 py-6">
        <h3
          className="font-display uppercase text-2xl sm:text-3xl text-[#ededed] leading-tight"
          style={{ letterSpacing: "-0.005em" }}
        >
          {artist.name}
        </h3>
        <p className="mt-2 font-body text-sm text-[#a1a1aa] leading-relaxed">
          {artist.tagline}
        </p>

        <div className="mt-4 space-y-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.32em] text-[#52525b]">
            Lane
          </div>
          <div className="font-body text-xs text-[#c8c8ce]">{artist.lane}</div>
        </div>

        <div className="mt-3 space-y-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.32em] text-[#52525b]">
            Reference palette
          </div>
          <div className="font-body text-xs text-[#c8c8ce] italic">
            {artist.reference}
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
          <span
            className="font-mono text-[10px] uppercase tracking-[0.28em]"
            style={{ color: playsDisabled ? "#52525b" : artist.accent }}
          >
            {(playsDisabled || playableTracks.length === 0)
              ? "Tracks · bake pending"
              : `${artist.trackCount} tracks`}
          </span>
          {!playsDisabled && playableTracks.length > 0 && (
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#52525b]">
              {playableTracks.length} previews
            </span>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {playableTracks.map((t) => {
            const active = t.slug === currentTrackSlug;
            return (
              <button
                key={t.slug}
                type="button"
                data-testid={`artist-track-${t.slug}`}
                onClick={() =>
                  !playsDisabled && onPlayTrack(artist.slug, t.slug)
                }
                disabled={playsDisabled}
                title={
                  playsDisabled
                    ? "Track catalog rebuilding · refresh once the bake lands"
                    : ""
                }
                className={`font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border transition-colors ${
                  playsDisabled
                    ? "text-[#52525b] border-white/[0.05] opacity-50 cursor-not-allowed"
                    : active
                    ? "text-[#ff5a00] border-[#ff5a00]/70"
                    : "text-[#a1a1aa] hover:text-[#ff5a00] border-white/[0.07] hover:border-[#ff5a00]/50"
                }`}
              >
                {t.title}
              </button>
            );
          })}
        </div>

        <div className="mt-5 pt-4 border-t border-white/[0.05]">
          <Link
            to={`/artists/${artist.slug}`}
            data-testid={`artist-deeplink-${artist.slug}`}
            className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa] hover:text-white transition-colors flex items-center justify-between group/link"
          >
            <span>Open the deep page</span>
            <span
              className="transition-transform group-hover/link:translate-x-1"
              style={{ color: artist.accent }}
            >
              →
            </span>
          </Link>
        </div>
      </div>
    </motion.article>
  );
}